import { useSelector } from 'react-redux';
import { Button, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { Password, Receipt, FiberManualRecord } from '@mui/icons-material';
import useTaxInvoiceInfo from '../hooks/useTaxInvoiceInfo';
import { H4, Body1 } from '../../../../../../themes/typography';

// TODO: Only load this component if tax invoice details provided or filled. Else show the "Add Tax Invoice Details" component with popup to add tax invoice details. [Done]
// TODO: Show added tax invoice details in the form fields as pre-selected values. [Need Backend]
// TODO: Load all the sac code option in select sac code input. [Need Backend]
// TODO: As user selects any SAC code then submit that on Save Changes button click to backend and load that option as pre-selected in sac code input. [Need Backend]

// city_id, state_id, country_id are receiving null after filling the form and submitting it. [Fix in backend or response]

export default function TaxInvoiceInfo() {
    const { formik, loading } = useTaxInvoiceInfo();
    const { sacCodes } = useSelector((state) => state.gst);
    const { countries, allCities, allStates } = useSelector((state) => state.cities);

    const codes = [...(sacCodes?.data || [])].sort((a, b) => {
        if (a.description === null && b.description !== null) return 1;
        if (a.description !== null && b.description === null) return -1;
        return 0;
    });

    // function to get the label for the option that is selected from the dropdown
    function getOptionLabel(option) {
        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(option.description || '', 'text/html');
        const boldText = parsedHTML.querySelector('b')?.textContent?.trim().replace(/:$/, '') || '';
        return `${option.sac_code || ''} - ${boldText}`;
    }

    // function to render the option in the dropdown
    function renderOption(props, option) {
        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(option.description || '', 'text/html');
        const title = parsedHTML.querySelector('b')?.textContent?.trim() || '';
        const rest = parsedHTML.body.innerHTML
            .replace(/<b>.*?<\/b>/gi, '') // remove bold tag contents
            .replace(/<[^>]+>/g, '') // remove all other HTML tags
            .replace(/:/g, '') // remove colons
            .trim();

        return (
            <li
                {...props}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
                <span>
                    <strong>{option.sac_code}</strong>
                    {title ? ` - ${title}` : ''}
                </span>
                {rest && <span style={{ fontSize: '0.875rem', color: '#666' }}>{rest}</span>}
            </li>
        );
    }

    return (
        <form className="bank-info-container" onSubmit={formik.handleSubmit}>
            <div className="bank-info-main">
                <div className="invoice-info-main-container">
                    <div id="personal-info-form">
                        <div className="invoice-info-header">
                            <div className="invoice-info-title">
                                <span className="invoice-info-icon">
                                    <Password color="secondary" sx={{ fontSize: '1.75rem' }} />
                                </span>
                                <H4 fontWeight={600}>SAC Code Information</H4>
                            </div>
                        </div>
                        <div className="invoice-info-body">
                            <div className="invoice-info-details">
                                <div
                                    className="invoice-info-two-inputs"
                                    style={{ alignItems: 'center' }}
                                >
                                    <div className="invoice-info-input-field">
                                        <Autocomplete
                                            fullWidth
                                            id="selectSacCode"
                                            name="selectSacCode"
                                            // options={sacCodes?.data || []}
                                            options={codes}
                                            getOptionLabel={getOptionLabel}
                                            renderOption={renderOption}
                                            value={formik.values.sacCode}
                                            onChange={(event, value) =>
                                                formik.setFieldValue('sacCode', value)
                                            }
                                            renderInput={(params) => (
                                                <TextField {...params} label="Select SAC Code" />
                                            )}
                                        />
                                    </div>
                                    <div
                                        className="invoice-info-input-field"
                                        style={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <Body1>
                                            <FiberManualRecord
                                                color="secondary"
                                                sx={{ fontSize: '0.75rem', marginRight: '0.5rem' }}
                                            />
                                            Want to know more about what SAC code is?{' '}
                                            <a
                                                href="#something"
                                                style={{
                                                    color: '#26c1e8',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                Click here
                                            </a>
                                        </Body1>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="selected-bank-info">
                    <div className="invoice-info-main-container">
                        <div id="personal-info-form">
                            <div className="invoice-info-header">
                                <div className="invoice-info-title">
                                    <span className="invoice-info-icon">
                                        <Receipt color="secondary" sx={{ fontSize: '1.75rem' }} />
                                    </span>
                                    <H4 fontWeight={600}>Tax Invoice Information</H4>
                                </div>
                            </div>
                            <div className="invoice-info-body">
                                <div className="invoice-info-details">
                                    <div className="invoice-info-two-inputs">
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                id="panNumber"
                                                name="panNumber"
                                                label="PAN Number"
                                                value={formik.values.panNumber}
                                            />
                                        </div>
                                        <div className="invoice-info-input-field">
                                            {formik.values.gstNumber && (
                                                <TextField
                                                    disabled
                                                    fullWidth
                                                    id="gstNumber"
                                                    name="gstNumber"
                                                    label="GST Number"
                                                    value={formik.values.gstNumber}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="invoice-info-two-inputs">
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                label="Address"
                                                name="address"
                                                id="address"
                                                value={formik.values.address}
                                            />
                                        </div>
                                    </div>
                                    <div className="invoice-info-two-inputs">
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                id="city"
                                                name="city"
                                                label="City"
                                                value={
                                                    allCities.find(
                                                        (city) => city.id === formik.values.city
                                                    )?.name || ''
                                                }
                                            />
                                        </div>
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                id="state"
                                                name="state"
                                                label="State"
                                                value={
                                                    allStates.find(
                                                        (state) => state.id === formik.values.state
                                                    )?.name || ''
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="invoice-info-two-inputs">
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                id="pincode"
                                                name="pincode"
                                                label="Pin Code"
                                                value={formik.values.pincode}
                                            />
                                        </div>
                                        <div className="invoice-info-input-field">
                                            <TextField
                                                disabled
                                                fullWidth
                                                id="country"
                                                name="country"
                                                label="Country"
                                                value={
                                                    countries.find(
                                                        (country) =>
                                                            country.id === formik.values.country
                                                    )?.name || ''
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bank-info-save-changes">
                <Button variant="main" type="submit" disabled={loading || !formik.values.sacCode}>
                    Save Changes
                    {loading && (
                        <CircularProgress
                            size={20}
                            sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                        />
                    )}
                </Button>
            </div>
        </form>
    );
}
